import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const couponModalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  content: {
    position: "relative",
    inset: "unset",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    transform: "none",
    width: "min(640px, 100%)",
    maxHeight: "min(90vh, 720px)",
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
};

function formatCouponCodeInput(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/\s+/g, "-")
    .replace(/[^A-Z0-9_-]/g, "")
    .replace(/-+/g, "-");
}

const emptyModalData = {
  Code: "",
  DiscountType: "percent",
  DiscountValue: "",
  MaxRedemptions: "",
  ValidFrom: "",
  ValidTo: "",
  PackageID: "",
  Status: true,
  Notes: "",
};

function formatDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDiscount(coupon) {
  if (coupon.DiscountType === "fixed") {
    return `$${Number(coupon.DiscountValue || 0).toFixed(2)} off`;
  }
  return `${Number(coupon.DiscountValue || 0)}% off`;
}

const ManageCoupons = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalData, setModalData] = useState(emptyModalData);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [packages, setPackages] = useState([]);
  const [viewRedemptionsFor, setViewRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsTotal, setRedemptionsTotal] = useState(0);
  const [redemptionsPage, setRedemptionsPage] = useState(1);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);

  const fetchCouponsPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get("/api/Coupons/GetAll", {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      throw error;
    }
  }, []);

  const {
    rows: coupons,
    totalCount,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchCouponsPage);

  useEffect(() => {
    axiosInstance
      .get("/api/Packages/GetAll")
      .then((res) => setPackages(res.data?.data || res.data || []))
      .catch(() => setPackages([]));
  }, []);

  const loadRedemptions = useCallback(async (couponId, page = 1) => {
    setRedemptionsLoading(true);
    try {
      const res = await axiosInstance.get("/api/Coupons/Redemptions", {
        params: { couponId, page, limit: 10 },
      });
      setRedemptions(res.data?.data || []);
      setRedemptionsTotal(res.data?.total || 0);
      setRedemptionsPage(page);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "Could not load redemptions.");
    } finally {
      setRedemptionsLoading(false);
    }
  }, []);

  const openModal = (data = null) => {
    if (data) {
      setIsEditing(true);
      setEditId(data.id);
      setModalData({
        Code: data.Code || "",
        DiscountType: data.DiscountType || "percent",
        DiscountValue: data.DiscountValue ?? "",
        MaxRedemptions: data.MaxRedemptions ?? "",
        ValidFrom: formatDateInput(data.ValidFrom),
        ValidTo: formatDateInput(data.ValidTo),
        PackageID: data.PackageID ?? "",
        Status: data.Status !== false,
        Notes: data.Notes || "",
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setModalData(emptyModalData);
    }
    setModalIsOpen(true);
  };

  const closeModal = () => setModalIsOpen(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setModalData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "Code"
            ? formatCouponCodeInput(value)
            : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !modalData.Code.trim()) {
      showErrorToast("Coupon code is required");
      return;
    }
    const discountValue = parseFloat(modalData.DiscountValue);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      showErrorToast("Discount value must be greater than zero");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...modalData,
        DiscountValue: discountValue,
        MaxRedemptions: modalData.MaxRedemptions === "" ? null : modalData.MaxRedemptions,
        PackageID: modalData.PackageID === "" ? null : modalData.PackageID,
        ValidFrom: modalData.ValidFrom || null,
        ValidTo: modalData.ValidTo || null,
      };

      if (isEditing) {
        await axiosInstance.put(`/api/Coupons/Update/${editId}`, payload);
        showSuccessToast("Coupon updated successfully");
      } else {
        await axiosInstance.post("/api/Coupons/Add", payload);
        showSuccessToast("Coupon created successfully");
      }
      closeModal();
      await refetch();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (couponId) => {
    const result = await Swal.fire({
      title: "Delete coupon?",
      text: "Only unused coupons can be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      setIsSubmitting(true);
      await axiosInstance.delete(`/api/Coupons/Delete/${couponId}`);
      showSuccessToast("Coupon deleted");
      if (viewRedemptionsFor?.id === couponId) {
        setViewRedemptionsFor(null);
      }
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not delete coupon.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRedemptions = (coupon) => {
    setViewRedemptionsFor(coupon);
    loadRedemptions(coupon.id, 1);
  };

  if (isLoading && coupons.length === 0) {
    return <Preloader />;
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Coupons</li>
          </ul>
          <h1 className="page-header mb-0">Coupons</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-success btn-rounded px-4 rounded-pill"
            onClick={() => openModal()}
            aria-label="Add coupon"
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true" />
            Add
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Package</th>
                <th>Valid</th>
                <th>Usage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5">
                    Loading…
                  </td>
                </tr>
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5">
                    No coupons yet.
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td className="fw-semibold">{coupon.Code}</td>
                    <td>{formatDiscount(coupon)}</td>
                    <td>{coupon.package?.Name || "All packages"}</td>
                    <td className="small">
                      {coupon.ValidFrom || coupon.ValidTo ? (
                        <>
                          {coupon.ValidFrom
                            ? new Date(coupon.ValidFrom).toLocaleDateString()
                            : "—"}
                          {" → "}
                          {coupon.ValidTo
                            ? new Date(coupon.ValidTo).toLocaleDateString()
                            : "—"}
                        </>
                      ) : (
                        "Always"
                      )}
                    </td>
                    <td>
                      {coupon.RedemptionCount ?? 0}
                      {coupon.MaxRedemptions != null ? ` / ${coupon.MaxRedemptions}` : ""}
                    </td>
                    <td>
                      {coupon.Status ? (
                        <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                          <i className="fa fa-circle fs-9px fa-fw me-5px" />
                          Active
                        </span>
                      ) : (
                        <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                          <i className="fa fa-circle fs-9px fa-fw me-5px" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        role="button"
                        tabIndex={0}
                        title="View usage"
                        onClick={() => openRedemptions(coupon)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") openRedemptions(coupon);
                        }}
                      >
                        <i className="fa fa-list fs-13px fa-fw me-5px" />
                      </span>
                      &nbsp;&nbsp;
                      <span
                        role="button"
                        tabIndex={0}
                        title="Edit coupon"
                        onClick={() => openModal(coupon)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") openModal(coupon);
                        }}
                      >
                        <i className="fa fa-pencil fs-13px fa-fw me-5px" />
                      </span>
                      &nbsp;&nbsp;
                      <span
                        role="button"
                        tabIndex={0}
                        title="Delete coupon"
                        onClick={() => handleDelete(coupon.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") handleDelete(coupon.id);
                        }}
                      >
                        <i className="fa fa-trash fs-13px fa-fw me-5px" />
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <ListPagination
            currentPage={currentPage}
            totalCount={totalCount}
            entriesPerPage={entriesPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {viewRedemptionsFor && (
        <div className="card border-0 mt-3">
          <div className="tab-content p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="h5 mb-0 fw-semibold">
                Redemptions — {viewRedemptionsFor.Code}
              </h2>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-3"
                onClick={() => setViewRedemptionsFor(null)}
              >
                Close
              </button>
            </div>
            {redemptionsLoading ? (
              <Preloader />
            ) : (
              <>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Package</th>
                      <th>Original</th>
                      <th>Discount</th>
                      <th>Final</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redemptions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-muted text-center py-4">
                          No redemptions yet.
                        </td>
                      </tr>
                    ) : (
                      redemptions.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <div>{row.fisher?.FullName || "—"}</div>
                            <div className="small text-muted">{row.fisher?.Email || ""}</div>
                          </td>
                          <td>{row.package?.Name || "—"}</td>
                          <td>${Number(row.OriginalAmount || 0).toFixed(2)}</td>
                          <td className="text-success">
                            -${Number(row.DiscountAmount || 0).toFixed(2)}
                          </td>
                          <td>${Number(row.FinalAmount || 0).toFixed(2)}</td>
                          <td className="small">
                            {row.RedeemedAt
                              ? new Date(row.RedeemedAt).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {redemptionsTotal > 10 && (
                  <ListPagination
                    currentPage={redemptionsPage}
                    totalCount={redemptionsTotal}
                    entriesPerPage={10}
                    onPageChange={(page) => loadRedemptions(viewRedemptionsFor.id, page)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={couponModalStyles}
        className="donezo-form-modal"
        overlayClassName="donezo-form-modal-overlay"
        contentLabel={isEditing ? "Edit coupon" : "Create coupon"}
      >
        <form
          onSubmit={handleSubmit}
          className="d-flex flex-column h-100"
          style={{ maxHeight: "min(90vh, 720px)" }}
        >
          <div className="donezo-form-modal__header">
            <span className="donezo-form-modal__icon" aria-hidden>
              <i className="fa-solid fa-ticket" />
            </span>
            <div className="flex-grow-1 min-w-0">
              <h2 className="donezo-form-modal__title">
                {isEditing ? "Edit Coupon" : "Create Coupon"}
              </h2>
              <p className="donezo-form-modal__subtitle">
                {isEditing
                  ? "Update validity dates, notes, or deactivate this coupon."
                  : "Set a discount code for first-time subscription billing."}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-link donezo-form-modal__close p-2"
              onClick={closeModal}
              aria-label="Close"
            >
              <i className="fas fa-times fa-lg" />
            </button>
          </div>

          <div className="donezo-form-modal__body">
            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">Coupon details</h3>
              <label htmlFor="coupon-code" className="form-label small fw-medium mb-1">
                Code {!isEditing && <span className="text-danger">*</span>}
              </label>
              <input
                type="text"
                className="form-control mb-3"
                id="coupon-code"
                name="Code"
                value={modalData.Code}
                onChange={handleChange}
                placeholder="FIRST-YEAR-FREE"
                disabled={isEditing}
                required={!isEditing}
                autoFocus={!isEditing}
                pattern="[A-Za-z0-9_-]{2,50}"
                title="Letters, numbers, hyphens, and underscores only"
              />
              {!isEditing && (
                <div className="form-text mb-3">
                  Spaces are converted to hyphens (e.g. FIRST YEAR FREE → FIRST-YEAR-FREE).
                </div>
              )}
              <div className="row g-3">
                <div className="col-sm-6">
                  <label htmlFor="coupon-discount-type" className="form-label small fw-medium mb-1">
                    Discount type
                  </label>
                  <select
                    className="form-select"
                    id="coupon-discount-type"
                    name="DiscountType"
                    value={modalData.DiscountType}
                    onChange={handleChange}
                    disabled={isEditing}
                  >
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div className="col-sm-6">
                  <label htmlFor="coupon-discount-value" className="form-label small fw-medium mb-1">
                    {modalData.DiscountType === "percent" ? "Percent off" : "Amount off (USD)"}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="coupon-discount-value"
                    name="DiscountValue"
                    value={modalData.DiscountValue}
                    onChange={handleChange}
                    min={modalData.DiscountType === "percent" ? "1" : "0.01"}
                    max={modalData.DiscountType === "percent" ? "100" : undefined}
                    step={modalData.DiscountType === "percent" ? "1" : "0.01"}
                    disabled={isEditing}
                    required
                  />
                </div>
              </div>
            </section>

            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">Restrictions</h3>
              <label htmlFor="coupon-package" className="form-label small fw-medium mb-1">
                Package restriction
              </label>
              <select
                className="form-select mb-3"
                id="coupon-package"
                name="PackageID"
                value={modalData.PackageID}
                onChange={handleChange}
                disabled={isEditing}
              >
                <option value="">All packages</option>
                {packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.Name}
                  </option>
                ))}
              </select>
              {!isEditing && (
                <>
                  <label htmlFor="coupon-max-redemptions" className="form-label small fw-medium mb-1">
                    Max redemptions (optional)
                  </label>
                  <input
                    type="number"
                    className="form-control mb-3"
                    id="coupon-max-redemptions"
                    name="MaxRedemptions"
                    value={modalData.MaxRedemptions}
                    onChange={handleChange}
                    min="1"
                  />
                </>
              )}
              <div className="row g-3">
                <div className="col-sm-6">
                  <label htmlFor="coupon-valid-from" className="form-label small fw-medium mb-1">
                    Valid from
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="coupon-valid-from"
                    name="ValidFrom"
                    value={modalData.ValidFrom}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-sm-6">
                  <label htmlFor="coupon-valid-to" className="form-label small fw-medium mb-1">
                    Valid to
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="coupon-valid-to"
                    name="ValidTo"
                    value={modalData.ValidTo}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">Notes &amp; availability</h3>
              {isEditing && (
                <div className="form-check form-switch mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id="coupon-status"
                    name="Status"
                    checked={modalData.Status}
                    onChange={handleChange}
                  />
                  <label className="form-check-label fw-medium" htmlFor="coupon-status">
                    Active
                  </label>
                </div>
              )}
              <label htmlFor="coupon-notes" className="form-label small fw-medium mb-1">
                Notes
              </label>
              <textarea
                className="form-control"
                id="coupon-notes"
                name="Notes"
                rows={2}
                value={modalData.Notes}
                onChange={handleChange}
                placeholder="Internal notes (optional)"
              />
              {!isEditing && (
                <p className="small text-muted mb-0 mt-3">
                  Discount applies to the first billing charge only. Renewals are at full price.
                </p>
              )}
            </section>
          </div>

          <div className="donezo-form-modal__footer">
            <button
              type="button"
              className="btn btn-outline-secondary px-4"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary px-4" disabled={isSubmitting}>
              <i
                className={`fas ${isSubmitting ? "fa-spinner fa-spin" : isEditing ? "fa-check" : "fa-plus"} me-2`}
                aria-hidden
              />
              {isSubmitting ? "Saving…" : isEditing ? "Save changes" : "Create coupon"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageCoupons;
